import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Page } from './ui';
import { Modal } from '../common/ui-component/modals';

interface Props {
  onClose: () => void;
  onTakePicture: (picture: string) => void;
  setCameraDetectedAndAllowed: (value: boolean) => void;
  useFrontCamera?: boolean;
}

const TakePicture = ({ onClose, onTakePicture, setCameraDetectedAndAllowed, useFrontCamera }: Props) => {
  const { t } = useTranslation('photosPage');
  const webcamRef = useRef<Webcam>(null);
  const [img, setImg] = useState<string | null>(null);
  const [isTakePictureModalOpen, setIsTakePictureModalOpen] = useState(false);

  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: useFrontCamera ? 'user' : 'environment' } })
      .then((stream) => {
        setCameraDetectedAndAllowed(true);
        setIsTakePictureModalOpen(true);
        const tracks = stream.getTracks();

        tracks.forEach((track) => {
          track.stop();
        });
      })
      .catch((err) => {
        if (err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
          setCameraDetectedAndAllowed(false);
          setIsTakePictureModalOpen(false);
          onClose();
        }
      });
  }, [onClose, setCameraDetectedAndAllowed, useFrontCamera]);

  const capture = useCallback(() => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    setImg(imageSrc);
  }, [webcamRef]);

  const handleSendClick = () => {
    if (!img) return;
    onTakePicture(img);
  };

  const handleCloseModal = () => {
    setIsTakePictureModalOpen(false);
    onClose();
  };

  const isShowingOverlay = !isMobile;
  return (
    <>
      {isShowingOverlay && (
        <div
          className="tw-absolute tw-top-0 tw-left-0
          tw-w-screen tw-h-screen tw-bg-black tw-opacity-95
        tw-flex tw-items-center tw-justify-center
        tw-text-white tw-font-inter tw-font-semibold tw-pb-3
         tw-z-9999"
        >
          {t('pleaseGrantPermissionToAccessYourWebcam')}
        </div>
      )}
      <Modal isOpen={isTakePictureModalOpen} onClose={handleCloseModal}>
        <>
          {img === null ? (
            <div className="tw-bg-gray-500 tw-w-full tw-flex tw-justify-center tw-align-middle">
              <Webcam
                audio={false}
                videoConstraints={{ facingMode: useFrontCamera ? 'user' : 'environment' }}
                screenshotFormat="image/jpeg"
                height={200}
                width={200}
                ref={webcamRef}
              />
            </div>
          ) : (
            <img
              className="tw-bg-gray-500 tw-w-full tw-flex tw-justify-center tw-align-middle"
              src={img}
              alt="screenshot"
            />
          )}
          <Page.TakePictureBox>
            <Page.TakePictureBox.Container>
              {img === null ? (
                <Page.TakePictureBox.Container.Action>
                  <Page.Button onClick={capture}>
                    <Page.Button.Secondary>{t('capture')}</Page.Button.Secondary>
                  </Page.Button>
                </Page.TakePictureBox.Container.Action>
              ) : (
                <Page.TakePictureBox.Container.Action>
                  <Page.Button onClick={() => setImg(null)}>
                    <Page.Button.Custom
                      className="tw-border tw-border-solid tw-border-black
               tw-bg-white tw-text-black tw-hover:tw-text-black"
                    >
                      {t('recapture')}
                    </Page.Button.Custom>
                  </Page.Button>
                  <Page.Button onClick={handleSendClick}>
                    <Page.Button.Secondary>{t('done')}</Page.Button.Secondary>
                  </Page.Button>
                </Page.TakePictureBox.Container.Action>
              )}
            </Page.TakePictureBox.Container>
          </Page.TakePictureBox>
        </>
      </Modal>
    </>
  );
};

export default TakePicture;
